import { isEssentialOnly } from "@/lib/essential-only";
// SNOWCLUB-V101-PRODUCTION-ANALYTICS-FUNNEL-20260929-001 — the GA4 recommended events other than
// `purchase`, sent through the SAME gates as everything else that reaches G-YMTRKT89WF.
//
// THE GATES (identical to @/lib/analytics-purchase):
//   1. production host only  — `isLiveHost` (snowbus.com / www.snowbus.com); staging/preview send nothing.
//   2. the tag already exists — @/lib/analytics-init owns loading gtag.js. This module never creates a
//      dataLayer, never queues an event for later and never loads the tag itself, so a pre-init action is
//      not measured. No visitor choice gates it: ordinary product analytics need no opt-in.
// There is no page_view here: `config` already sends the one page view per load (see Analytics.tsx).
//
// WHAT IS SENT: GA4's documented recommended-event names and parameters only, and never anything about a
// person — no email, name, phone, traveller detail, order or draft-order id. Items carry a product/leg id,
// a title, a supplier and a price; `login` / `sign_up` carry only the method.
//
// WHAT IS NOT HERE, ON PURPOSE (each needs a real action this build does not have — see the packet
// report): `purchase` on the authorize-only card path and `refund`. The design bundle's own cart adds and
// removals reach this module as `sg:cart` messages (see @/lib/analytics-design-cart), never by watching
// the `sg-cart-v1` storage the design writes.
import { adInitiateCheckout, adViewContent } from "@/lib/ad-pixels";
import { isLiveHost } from "@/lib/analytics-init";
import type { PurchaseItem } from "@/lib/analytics-purchase";

/** GA4 item shape — the same one `purchase` uses, so a funnel's items are comparable end to end. */
export type GaItem = PurchaseItem;

export type AnalyticsEventOutcome = "sent" | "no-window" | "not-live-host" | "tag-not-initialised" | "send-failed";

/** Every fare on this site is quoted in CAD (same default as `purchase`). */
const CURRENCY = "CAD";

/** The three gates without sending — for a caller that must decide whether to leave a handoff for a later page. */
export function analyticsGate(): "open" | Exclude<AnalyticsEventOutcome, "sent" | "send-failed"> {
  if (typeof window === "undefined") return "no-window";
  if (!isLiveHost(window.location.hostname)) return "not-live-host";
  if (typeof (window as { gtag?: unknown }).gtag !== "function") return "tag-not-initialised";
  // Essential-only (youth): GA4 is switched off on this page, so no event is sent.
  if (isEssentialOnly()) return "tag-not-initialised";
  return "open";
}

/** Send one GA4 event if — and only if — all three gates are open. Never throws: measurement must not
 *  be able to break a cart, a sign-in or a checkout. */
export function sendAnalyticsEvent(name: string, params: Record<string, unknown>): AnalyticsEventOutcome {
  const gate = analyticsGate();
  if (gate !== "open") return gate;
  const gtag = (window as unknown as { gtag: (...args: unknown[]) => void }).gtag;
  try {
    gtag("event", name, params);
  } catch {
    /* an extension replaced gtag, or the tag is mid-teardown: one lost event, never a thrown error */
    return "send-failed";
  }
  return "sent";
}

const price = (n: number | null | undefined): number => (typeof n === "number" && Number.isFinite(n) && n >= 0 ? n : 0);

/** Only fields GA4 defines, and only when the source actually has them. */
function item(id: string, name: string, quantity: number, unit: number | null | undefined, brand?: string, category?: string): GaItem {
  return {
    item_id: id,
    item_name: name,
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    price: price(unit),
    ...(brand ? { item_brand: brand } : {}),
    ...(category ? { item_category: category } : {}),
  };
}

/** A host-store cart line. A line with no published fare reports `price: 0` (GA4 needs a number). */
export function gaItemFromCartItem(i: {
  id: string;
  title: string;
  qty: number;
  guestPrice: number | null;
  supplier?: string;
  section?: string;
  category?: string;
}): GaItem {
  return item(i.id, i.title, i.qty, i.guestPrice, i.supplier, i.section === "transport" ? "transport" : i.category ?? "marketplace");
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayText = (v: unknown) => (typeof v === "string" ? v.trim() : "");

/**
 * The design's own inclusive day count — a host copy of SG_LINE_DAYS in the compiled design (which cannot be
 * imported). A rental held Dec 19 → Dec 20 is TWO days, the same date is ONE; transport, non-rentals and any
 * line without both real ISO dates are ONE day, so nothing is invented to fill a gap. The design's
 * SG_LINE_TOTAL is qty × price × this, and GA4's per-unit price for such a line must be price × this.
 */
export function designLineDays(l: { cat?: string; start?: string; startDate?: string; date?: string; end?: string; endDate?: string }): number {
  if (!l || l.cat === "Transport" || !/rental|\bski\b|snowboard/i.test(`${l.cat ?? ""} `)) return 1;
  const start = dayText(l.start) || dayText(l.startDate) || dayText(l.date);
  const end = dayText(l.end) || dayText(l.endDate);
  if (!ISO_DAY.test(start) || !ISO_DAY.test(end)) return 1;
  const ms = Date.UTC(+end.slice(0, 4), +end.slice(5, 7) - 1, +end.slice(8, 10)) - Date.UTC(+start.slice(0, 4), +start.slice(5, 7) - 1, +start.slice(8, 10));
  return Number.isFinite(ms) && ms >= 0 ? Math.floor(ms / 86_400_000) + 1 : 1;
}

/**
 * A line the design bundle persisted in `sg-cart-v1`, or one it announced as an `sg:cart` message.
 * A persisted rental carries its DAILY price and its own start/end, so its GA4 unit price is the daily price
 * × `designLineDays` — the figure the design itself totals. An `sg:cart` announcement carries no dates, so it
 * is reported as given and is never multiplied a second time.
 */
export function gaItemFromDesignLine(l: {
  id: string;
  name?: string;
  cat?: string;
  vendor?: string;
  qty: number;
  price: number;
  start?: string;
  startDate?: string;
  date?: string;
  end?: string;
  endDate?: string;
}): GaItem {
  const days = designLineDays(l);
  const unit = days > 1 && typeof l.price === "number" ? Math.round(l.price * days * 100) / 100 : l.price;
  return item(l.id, l.name ?? l.cat ?? "Item", Number(l.qty), unit, l.vendor, l.cat ? l.cat.toLowerCase() : undefined);
}

/** A marketplace product the visitor opened (quick view). */
export function gaItemFromProduct(p: { key: string; listingId?: string | null; name: string; guestPrice: number | null }, supplier: string): GaItem {
  // `??` would let an empty-string listingId through as item_id; "" must fall back to the key like null does.
  return item(p.listingId?.length ? p.listingId : p.key, p.name, 1, p.guestPrice, supplier, "marketplace");
}

/** value = Σ quantity × price, with the currency GA4 requires alongside it. */
function withValue(items: GaItem[]) {
  return { currency: CURRENCY, value: items.reduce((sum, i) => sum + i.quantity * i.price, 0) };
}

/** GA4 `login` — after the server issued a session. `method` names the mechanism, not the person. */
export const trackLogin = (method = "email") => sendAnalyticsEvent("login", { method });

/** GA4 `sign_up` — after the server created the account. */
export const trackSignUp = (method = "email") => sendAnalyticsEvent("sign_up", { method });

/** GA4 `add_to_cart` — one line the visitor added. */
export const trackAddToCart = (added: GaItem) => sendAnalyticsEvent("add_to_cart", { ...withValue([added]), items: [added] });

/** GA4 `remove_from_cart` — lines the visitor removed. */
export const trackRemoveFromCart = (removed: GaItem[]) => {
  if (removed.length === 0) return "empty" as const; // nothing was removed: nothing to report
  return sendAnalyticsEvent("remove_from_cart", { ...withValue(removed), items: removed });
};

/** GA4 `search` — a transport search the visitor submitted and this build handed to the booking funnel.
 *  The widget has no free-text field, so the term is the zone pair searched (`vancouver-to-whistler`):
 *  a route the visitor picked from a fixed list, never typed input and never anything about a person. */
export const trackSearch = (searchTerm: string) => {
  const term = searchTerm.trim();
  if (!term) return "empty" as const; // no term, no search to report
  return sendAnalyticsEvent("search", { search_term: term });
};

/** GA4 `view_item` — the visitor opened one product's detail. Also the advertising ViewContent seam (@/lib/ad-pixels,
 *  which applies its own advertising-consent gate). */
export const trackViewItem = (viewed: GaItem) => {
  adViewContent(viewed);
  return sendAnalyticsEvent("view_item", { ...withValue([viewed]), items: [viewed] });
};

/** GA4 `view_cart` — a non-empty cart the visitor is actually looking at. The CALLER decides what counts as
 *  one view (ExportCheckout: once per mounted review cart); an empty cart is never reported. value =
 *  Σ quantity × price, before tax, tip or fees, as GA4 recommends. */
export const trackViewCart = (shown: GaItem[]) => {
  if (shown.length === 0) return "empty" as const;
  return sendAnalyticsEvent("view_cart", { ...withValue(shown), items: shown });
};

// ── begin_checkout ────────────────────────────────────────────────────────────────────────────────
// Fired when the pay rail (/mountain-<code>/booking/review) opens with a non-empty cart. That page is
// reloaded, re-entered by Back/Forward and re-rendered, so the event is once per cart CONTENT per browser
// session: the fingerprint (line ids + quantities) is held in memory and in sessionStorage. Changing the
// cart is a new checkout attempt and reports again; a reload of the same cart does not.
const CHECKOUT_KEY = "sg.ga4.begin_checkout";
const startedThisLoad = new Set<string>();

export function trackBeginCheckout(items: GaItem[]): AnalyticsEventOutcome | "empty" | "duplicate" {
  if (items.length === 0) return "empty";
  if (typeof window === "undefined") return "no-window";
  // The advertising InitiateCheckout seam: its own consent gate and de-duplication (@/lib/ad-pixels), so a GA4
  // gate or a GA4 duplicate never decides it.
  adInitiateCheckout(items);
  const fingerprint = items
    .map((i) => `${i.item_id}x${i.quantity}`)
    .sort()
    .join("|");
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(CHECKOUT_KEY);
  } catch {
    /* storage blocked: the in-memory set still covers this page load */
  }
  if (startedThisLoad.has(fingerprint) || stored === fingerprint) return "duplicate";
  const outcome = sendAnalyticsEvent("begin_checkout", { ...withValue(items), items });
  // Recorded only once it was sent to gtag. A later storage grant must not duplicate the same checkout.
  if (outcome === "sent") {
    startedThisLoad.add(fingerprint);
    try {
      window.sessionStorage.setItem(CHECKOUT_KEY, fingerprint);
    } catch {
      /* see above */
    }
  }
  return outcome;
}

/**
 * The review cart on screen: view_cart (unless already sent for this mounted view) THEN begin_checkout, so
 * GA4 sees the funnel in order. Called on arrival and again on a fresh consent grant (CONSENT_GRANTED_EVENT).
 * De-duplication prevents the update from repeating events already offered in denied-storage mode. Returns
 * whether view_cart has now been sent; the caller
 * keeps that per mounted view. begin_checkout de-duplicates itself (sent calls only).
 */
export function reportReviewCart(shown: GaItem[], checkout: GaItem[], viewCartSent: boolean): boolean {
  const sent = viewCartSent || trackViewCart(shown) === "sent";
  trackBeginCheckout(checkout);
  return sent;
}

// ── contact_form_submit ───────────────────────────────────────────────────────────────────────────
// Founder 13:36 scope. Sent ONLY for a genuine receipt: FeedbackForm calls this after outcomeFromResponse
// returned "success", i.e. /api/feedback answered 2xx with ok:true and a well-formed case id. The case id is
// the de-duplication key and is NEVER sent: the event carries the form id and nothing the visitor typed. A
// re-render, a replayed response or the same receipt seen again (another tab, a reload that re-shows it) is
// not a new submission. Recorded only once it was sent, so a closed gate counts nothing.
const CONTACT_KEY = "sg.ga4.contact_receipts";
const CONTACT_MAX = 50;
const contactThisLoad = new Set<string>();

function contactLedger(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CONTACT_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function trackContactFormSubmit(caseId: string): AnalyticsEventOutcome | "duplicate" | "no-receipt" {
  const key = typeof caseId === "string" ? caseId.trim() : "";
  if (!key) return "no-receipt";
  if (typeof window === "undefined") return "no-window";
  if (contactThisLoad.has(key) || contactLedger().includes(key)) return "duplicate";
  const outcome = sendAnalyticsEvent("contact_form_submit", { form_id: "contact_us" });
  if (outcome === "sent") {
    contactThisLoad.add(key);
    try {
      window.localStorage.setItem(CONTACT_KEY, JSON.stringify(contactLedger().filter((x) => x !== key).concat(key).slice(-CONTACT_MAX)));
    } catch {
      /* storage blocked: the in-memory set still covers this page load */
    }
  }
  return outcome;
}

/** Test-only: module-level de-duplication state. */
export function _resetAnalyticsEventsForTests(): void {
  startedThisLoad.clear();
  contactThisLoad.clear();
  try {
    window.localStorage?.removeItem(CONTACT_KEY);
  } catch {
    /* nothing to clear */
  }
  try {
    window.sessionStorage.removeItem(CHECKOUT_KEY);
  } catch {
    /* nothing to clear */
  }
}
